/* Client Submissions — aggregated view of every email sent to a client with status & logs. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { Send, CheckCircle2, XCircle, Loader2, Search, Building2 } from "lucide-react";

export default function ClientSubmissions() {
  const [resumes, setResumes] = useState([]);
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const [r1, r2] = await Promise.all([
        api.get("/resumes"),
        api.get("/email/logs"),
      ]);
      setResumes(r1.data || []);
      setLogs(r2.data || []);
    })();
  }, []);

  const filtered = logs.filter((l) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (l.to_email || "").toLowerCase().includes(s) || (l.subject || "").toLowerCase().includes(s) || (l.resume?.candidate_name || "").toLowerCase().includes(s);
  });

  const statusIcon = (s) => s === "sent" ? <CheckCircle2 className="size-4 text-emerald-500" /> : s === "failed" ? <XCircle className="size-4 text-rose-500" /> : <Loader2 className="size-4 animate-spin" />;

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground font-semibold flex items-center gap-2"><Building2 className="size-3.5" /> Client Submissions</div>
        <h1 className="display text-3xl md:text-4xl font-black tracking-tighter mt-1">Outbound pipeline</h1>
        <p className="text-muted-foreground mt-1 text-sm">Every candidate profile you've sent to a client — with delivery status.</p>
      </div>

      <div className="mb-4 relative max-w-md">
        <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by recipient, subject, or candidate…" className="pl-9 h-10" data-testid="subs-search" />
      </div>

      {filtered.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-10 text-center">
            <Send className="size-12 text-muted-foreground mx-auto opacity-50" />
            <div className="display font-bold text-lg mt-3">No submissions yet</div>
            <div className="text-sm text-muted-foreground">Open any resume, click <kbd className="border rounded px-1">Send</kbd>, and your history will appear here.</div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-border">
          <CardContent className="p-0 divide-y divide-border">
            {filtered.map((l) => (
              <button key={l.id} onClick={() => navigate(`/editor/${l.resume_id}`)} className="w-full flex items-center gap-3 px-5 py-3 hover:bg-secondary text-left" data-testid={`sub-${l.id}`}>
                {statusIcon(l.status)}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{l.resume?.candidate_name || l.resume?.title || "Unknown candidate"}</div>
                  <div className="text-xs text-muted-foreground truncate">→ {l.to_email} · {l.subject}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground">{l.status}</div>
                  <div className="text-[11px] text-muted-foreground">{new Date(l.sent_at).toLocaleString()}</div>
                </div>
              </button>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
